import type { RiskAssessmentConfig } from '@safetyhub/shared';
import type { BoundingBox, InspectionFinding } from '../../../lib/inspections';
import { SeverityBadge } from './SeverityBadge';

/** 위험 영역 편집 상태 */
export interface HazardEditState {
  findingId: string;
  /** 원본 위험 영역 (되돌리기용) */
  original: BoundingBox | null;
  /** 현재 위험 영역 */
  current: BoundingBox | null;
  /** 삭제 여부 */
  isDeleted: boolean;
}

interface FindingsPanelProps {
  /** 발견사항 목록 */
  findings: InspectionFinding[];
  /** 현재 선택된 발견사항 ID */
  selectedFindingId?: string | null;
  /** 발견사항 선택 핸들러 */
  onFindingSelect?: (findingId: string | null) => void;
  /** 위험 영역 편집 상태 */
  hazardEdits?: HazardEditState[];
  /** 위험 영역 되돌리기 핸들러 */
  onHazardRestore?: (findingId: string) => void;
  /** Phase N3: 위험성평가 설정 (score 기반 SeverityBadge 렌더링용) */
  riskConfig?: RiskAssessmentConfig | null;
}

/**
 * 발견사항 목록 패널 (편집 모드용)
 *
 * - 발견사항 선택 시 해당 위험 영역 강조
 * - 되돌리기 버튼으로 원래 위치/크기 복원
 * - 삭제된 위험 영역도 표시 (흐리게)
 */
export function FindingsPanel({
  findings,
  selectedFindingId,
  onFindingSelect,
  hazardEdits = [],
  onHazardRestore,
  riskConfig,
}: FindingsPanelProps) {
  // 삭제되지 않은 활성 발견사항만 필터링
  const activeFindings = findings.filter((f) => !f.deletedAt);

  // 발견사항별 편집 상태 매핑
  const editStateMap = new Map(hazardEdits.map((e) => [e.findingId, e]));

  // 되돌리기가 필요한지 확인 (원본과 현재가 다르거나 삭제됨)
  const needsRestore = (finding: InspectionFinding): boolean => {
    const editState = editStateMap.get(finding.id);
    if (!editState) return false;

    // 삭제된 경우
    if (editState.isDeleted) return true;

    // 원본과 현재가 다른 경우
    if (editState.original && editState.current) {
      const orig = editState.original;
      const curr = editState.current;
      return (
        orig.x !== curr.x ||
        orig.y !== curr.y ||
        orig.width !== curr.width ||
        orig.height !== curr.height
      );
    }

    return false;
  };

  // 위험 영역 삭제 여부
  const isHazardDeleted = (findingId: string): boolean => {
    const editState = editStateMap.get(findingId);
    return editState?.isDeleted ?? false;
  };

  if (activeFindings.length === 0) {
    return (
      <div className="p-4 text-center text-ink-500 text-sm">
        발견된 위험요인가 없습니다.
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {/* 발견사항 목록 */}
      <div className="divide-y max-h-[200px] overflow-y-auto">
        {activeFindings.map((finding, index) => {
          const isSelected = selectedFindingId === finding.id;
          const hazardDeleted = isHazardDeleted(finding.id);
          const canRestore = needsRestore(finding);

          return (
            <div
              key={finding.id}
              className={`
                px-4 py-3 cursor-pointer transition-colors
                ${isSelected ? 'bg-brand-50 border-l-4 border-l-brand-500' : 'hover:bg-surface-hover'}
                ${hazardDeleted ? 'opacity-60' : ''}
              `}
              onClick={() => onFindingSelect?.(isSelected ? null : finding.id)}
            >
              <div className="flex items-start justify-between gap-2">
                {/* 발견사항 정보 */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-ink-900">#{index + 1}</span>
                    <SeverityBadge likelihoodScore={finding.aiLikelihoodScore} severityScore={finding.aiSeverityScore} config={riskConfig} size="sm" />
                    <span className="text-sm text-ink-700 truncate">
                      {finding.category}
                      {finding.subCategory && (
                        <span className="text-ink-400"> &gt; {finding.subCategory}</span>
                      )}
                    </span>
                    {hazardDeleted && (
                      <span className="text-xs text-ink-400">(영역 삭제됨)</span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-ink-500 line-clamp-2">
                    {finding.description}
                  </p>
                </div>

                {/* 되돌리기 버튼 */}
                {canRestore && onHazardRestore && (
                  <button
                    type="button"
                    className="flex-shrink-0 p-1.5 text-ink-400 hover:text-brand-600 hover:bg-brand-50 rounded transition-colors"
                    title="위험 영역 되돌리기"
                    onClick={(e) => {
                      e.stopPropagation();
                      onHazardRestore(finding.id);
                    }}
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default FindingsPanel;
